/**
 * T155P4 — RuleSuggestionChip
 *
 * Per-transaction chip shown when a dryRun preview row exists for this txn.
 * Lets the user apply the rule suggestion to this one transaction,
 * open it for editing, or dismiss without applying.
 *
 * Actions:
 *   Apply once  → calls onApply with the after-category
 *   Edit before apply → calls onEdit pre-filled with after-category
 *   Dismiss → hides chip for this transaction (session state)
 */
import { useState } from 'react';
import { Check, Pencil, Wand2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAppSelector } from '@/features/hooks';

interface RuleSuggestionChipProps {
  transactionId: string;
  onApply: (categoryId: string, categoryName: string) => void;
  onEdit: (categoryId: string, categoryName: string) => void;
}

export function RuleSuggestionChip({ transactionId, onApply, onEdit }: RuleSuggestionChipProps) {
  const [dismissed, setDismissed] = useState(false);

  const dryRunResult = useAppSelector((s) => s.transactionRules.dryRunResult);

  if (dismissed || !dryRunResult) return null;

  const row = dryRunResult.preview.find((r) => r.transactionId === transactionId);
  if (!row) return null;

  const afterCategory = row.after.category;
  if (!afterCategory || afterCategory === row.before.category) return null;

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap"
      data-testid={`rule-suggestion-chip-${transactionId}`}
    >
      <Badge
        variant="secondary"
        className="flex items-center gap-1 text-xs font-normal text-muted-foreground bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 max-w-[200px]"
      >
        <Wand2 className="size-3 shrink-0 text-violet-500" />
        <span className="truncate">
          Rule: <span className="font-medium text-foreground">{afterCategory}</span>
        </span>
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30"
        onClick={() => onApply(afterCategory, afterCategory)}
        data-testid={`rule-suggestion-apply-${transactionId}`}
      >
        <Check className="size-3 mr-1" />
        Apply once
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs"
        onClick={() => onEdit(afterCategory, afterCategory)}
        data-testid={`rule-suggestion-edit-${transactionId}`}
      >
        <Pencil className="size-3 mr-1" />
        Edit
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss rule suggestion"
        data-testid={`rule-suggestion-dismiss-${transactionId}`}
      >
        <X className="size-3" />
      </Button>
    </div>
  );
}
